<template>
    <CompassTooltip v-if="!decorative" :content="$t.__('common.labels.need_help')" :arrow="false">
        <button :id="elementId" class="meltingspot-widget">
            <CompassIcon icon="school" :size="CompassEnums.IconSize.SMALL" />
        </button>
    </CompassTooltip>
    <span v-else class="meltingspot-widget meltingspot-widget--decorative">
        <CompassIcon icon="school" :size="CompassEnums.IconSize.SMALL" />
    </span>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, inject } from 'vue';
import { useMeltingSpot } from 'composables/useMeltingSpot';
import { Translator } from 'types/injects/translator.type';

interface Props {
    widgetId?: string | null;
    decorative?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    widgetId: null,
    decorative: false,
});

const $t = inject('$t') as Translator;
const { load, bindDrawer, unbindDrawer, generateElementId } = useMeltingSpot();
const elementId = generateElementId();

onMounted(async () => {
    if (!props.decorative && props.widgetId) {
        await load();
        bindDrawer(elementId, props.widgetId);
    }
});

onBeforeUnmount(() => {
    unbindDrawer(elementId);
});
</script>

<style lang="scss" scoped>
.meltingspot-widget {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 5px;
    background: conic-gradient(
        from 76.19deg at 50% 50%,
        #fff2e6 -50.4deg,
        #e6fffc 144deg,
        #c0e8ff 190.8deg,
        #fff2e6 309.6deg,
        #e6fffc 504deg
    );
    color: var(--colors-sellsy-brand-primary-600);
    cursor: pointer;
    transition: color 0.2s ease-in-out;
    padding: 0;
    &::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: inherit;
        background-color: var(--colors-common-background-primary-subtle-alt);
        opacity: 0;
        transition: opacity 0.2s ease-in-out;
    }
    &:hover {
        color: var(--colors-sellsy-brand-primary-700);
        &::after {
            opacity: 1;
        }
    }
    &:focus {
        outline: none;
    }
    :deep(.compass-icon--small) {
        width: 18px;
        height: 18px;
    }
    &--decorative {
        cursor: pointer;
    }
}
</style>

<style lang="scss">
/* Overwrite styles for MeltingSpotWidget when used in NavProfilMenu */
.compass-menu-item .prefix-icon-container:has(.meltingspot-widget--decorative) {
    display: flex;
    align-items: center;

    .meltingspot-widget--decorative {
        width: 20px;
        height: 20px;
    }
    .compass-icon {
        width: 14px;
        height: 14px;
    }
}
</style>
